"use client";

import * as React from "react";
import { ChevronRight, LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

// ============================================================================
// ActionCard - Beautiful clickable action cards with proper text handling
// ============================================================================

export interface ActionCardProps {
  /** Title of the action */
  title: string;
  /** Description text (will be truncated to 2 lines) */
  description?: string;
  /** Icon component from lucide-react */
  icon?: LucideIcon;
  /** Visual variant */
  variant?: "default" | "primary" | "secondary" | "premium" | "warning";
  /** Optional badge text */
  badge?: string;
  /** Badge icon */
  badgeIcon?: LucideIcon;
  /** Click handler */
  onClick?: () => void;
  /** Disabled state */
  disabled?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Additional className */
  className?: string;
  /** Show arrow indicator on hover */
  showArrow?: boolean;
}

const variantStyles = {
  default: {
    container: "bg-card hover:bg-muted/50 border-border",
    icon: "bg-muted group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/30",
    iconColor: "text-muted-foreground group-hover:text-emerald-600 dark:group-hover:text-emerald-400",
    title: "text-foreground",
    description: "text-muted-foreground",
    arrow: "text-muted-foreground",
    badge: "",
  },
  primary: {
    container: "bg-gradient-to-br from-emerald-500 to-teal-600 text-white border-transparent hover:from-emerald-600 hover:to-teal-700",
    icon: "bg-white/20",
    iconColor: "text-white",
    title: "text-white",
    description: "text-white/80",
    arrow: "text-white/70",
    badge: "bg-white/20 text-white border-white/20",
  },
  secondary: {
    container: "bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border-blue-200 dark:border-blue-800 hover:border-blue-300 dark:hover:border-blue-700",
    icon: "bg-blue-100 dark:bg-blue-900/50",
    iconColor: "text-blue-600 dark:text-blue-400",
    title: "text-foreground",
    description: "text-muted-foreground",
    arrow: "text-blue-500",
    badge: "bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200",
  },
  premium: {
    container: "bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border-amber-200 dark:border-amber-800 hover:border-amber-300 dark:hover:border-amber-700",
    icon: "bg-amber-100 dark:bg-amber-900/50",
    iconColor: "text-amber-600 dark:text-amber-400",
    title: "text-foreground",
    description: "text-muted-foreground",
    arrow: "text-amber-500",
    badge: "bg-amber-200 text-amber-800 dark:bg-amber-800 dark:text-amber-200",
  },
  warning: {
    container: "bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-950/30 dark:to-rose-950/30 border-red-200 dark:border-red-800",
    icon: "bg-red-100 dark:bg-red-900/50",
    iconColor: "text-red-600 dark:text-red-400",
    title: "text-foreground",
    description: "text-muted-foreground",
    arrow: "text-red-500",
    badge: "bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-200",
  },
};

export function ActionCard({
  title,
  description,
  icon: Icon,
  variant = "default",
  badge,
  badgeIcon: BadgeIcon,
  onClick,
  disabled = false,
  loading = false,
  className,
  showArrow = true,
}: ActionCardProps) {
  const styles = variantStyles[variant];

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={cn(
        "group relative overflow-hidden rounded-xl p-4 text-left transition-all duration-200",
        "border hover:shadow-md hover:-translate-y-0.5",
        "focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2",
        "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none",
        styles.container,
        className
      )}
    >
      {/* Decorative corner accent */}
      <div className={cn(
        "absolute -top-8 -right-8 w-16 h-16 rounded-full opacity-20",
        variant === "primary" ? "bg-white" : "bg-emerald-500"
      )} />

      {/* Icon */}
      {Icon && (
        <div className={cn(
          "mb-3 inline-flex p-2 rounded-lg transition-colors",
          styles.icon
        )}>
          <Icon className={cn("h-5 w-5 flex-shrink-0", styles.iconColor)} />
        </div>
      )}

      {/* Title row */}
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <h3 className={cn(
          "font-semibold text-sm leading-tight line-clamp-1",
          styles.title
        )}>
          {title}
        </h3>
        {badge && (
          <Badge 
            variant="secondary" 
            className={cn("text-[10px] px-1.5 py-0 shrink-0", styles.badge)}
          >
            {BadgeIcon && <BadgeIcon className="h-2.5 w-2.5 mr-0.5" />}
            {badge}
          </Badge>
        )}
      </div>

      {/* Description with proper truncation */}
      {description && (
        <p className={cn(
          "text-xs leading-relaxed line-clamp-2",
          styles.description
        )}>
          {description}
        </p>
      )}

      {/* Hover indicator */}
      {showArrow && (
        <div className={cn(
          "absolute bottom-3 right-3 transition-all duration-200",
          "opacity-0 translate-x-1 group-hover:opacity-100 group-hover:translate-x-0"
        )}>
          <ChevronRight className={cn("h-4 w-4", styles.arrow)} />
        </div>
      )}

      {/* Loading indicator */}
      {loading && (
        <div className="absolute inset-x-0 bottom-0 h-1 bg-black/10">
          <div className="h-full bg-white/50 animate-pulse" style={{ width: "60%" }} />
        </div>
      )}
    </button>
  );
}

// ============================================================================
// ActionCardGrid - Container for action cards with responsive grid
// ============================================================================

export interface ActionCardGridProps {
  children: React.ReactNode;
  columns?: 2 | 3 | 4;
  className?: string;
}

export function ActionCardGrid({ 
  children, 
  columns = 3,
  className 
}: ActionCardGridProps) {
  const gridCols = {
    2: "sm:grid-cols-2",
    3: "sm:grid-cols-2 lg:grid-cols-3",
    4: "sm:grid-cols-2 lg:grid-cols-4",
  };

  return (
    <div className={cn("grid gap-3", gridCols[columns], className)}>
      {children}
    </div>
  );
}

